import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the spear-phishing-osint start screen.
 * Three short slides (what/who and why a human is the control, the red flags
 * in the chain, key terms) shown in the reader while the 3D room downloads.
 * Content only; the reader UI lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. public posts -> attacker's notes                                  */
/* ------------------------------------------------------------------ */
function PublicBadge({ x, y }: { x: number; y: number }): JSX.Element {
  return (
    <g>
      <rect x={x} y={y} width="58" height="20" rx="10" fill={C.amber} fillOpacity="0.12" stroke={C.amber} strokeOpacity="0.45" />
      <circle className="sr-anim-ring" cx={x + 14} cy={y + 10} r="4" fill="none" stroke={C.amber} strokeWidth="1.4" />
      <circle cx={x + 14} cy={y + 10} r="2" fill={C.amber} />
      <text x={x + 24} y={y + 14} fontSize="10.5" fontWeight="700" fill={C.amber}>
        Public
      </text>
    </g>
  );
}

function Stars({ x, y }: { x: number; y: number }): JSX.Element {
  const pts = [0, 1, 2, 3, 4];
  return (
    <g>
      {pts.map((i) => (
        <polygon
          key={i}
          transform={`translate(${x + i * 13} ${y})`}
          points="5,0 6.5,3.4 10,3.8 7.4,6.2 8.1,9.8 5,8 1.9,9.8 2.6,6.2 0,3.8 3.5,3.4"
          fill={C.amber}
        />
      ))}
    </g>
  );
}

export function OversharingArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Example: what you post, what they collect" aria="A public restaurant review and a public holiday photo, and below them an attacker's notes listing the restaurant, the family and the hobby" height={H}>
      {/* review post */}
      <rect x="16.5" y="36.5" width="327" height="62" rx="9" fill={C.panel2} stroke={C.line} />
      <circle cx="35" cy="54" r="10" fill={C.mint} fillOpacity="0.14" />
      <circle cx="35" cy="51" r="3.4" fill="none" stroke={C.mint} strokeWidth="1.5" />
      <path d="M28.5 61 a6.5 5 0 0 1 13 0" fill="none" stroke={C.mint} strokeWidth="1.5" strokeLinecap="round" />
      <text x="52" y="51" fontSize="12" fontWeight="700" fill={C.text}>
        Marco R.
      </text>
      <text x="52" y="64" fontSize="10.5" fill={C.muted}>
        Review
      </text>
      <Stars x={92} y={56} />
      <PublicBadge x={278} y={42} />
      <text x="28" y="85" fontSize="11.5" fill={C.soft}>
        Dinner was amazing. Thank you to Luca, the owner!
      </text>

      {/* holiday post */}
      <rect x="16.5" y="106.5" width="327" height="78" rx="9" fill={C.panel2} stroke={C.line} />
      <rect x="28" y="116" width="76" height="58" rx="6" fill={C.code} stroke={C.line} />
      <line x1="28" y1="156" x2="104" y2="156" stroke={C.mint} strokeOpacity="0.5" />
      <path d="M66 126 V152 L84 152 Z" fill="none" stroke={C.mint} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M62 128 L50 152 H62 Z" fill="none" stroke={C.mint} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M46 158 H88 L82 166 H52 Z" fill="none" stroke={C.mint} strokeWidth="1.5" strokeLinejoin="round" />
      <text x="116" y="132" fontSize="12" fontWeight="700" fill={C.text}>
        Marco R.
      </text>
      <text x="116" y="148" fontSize="11.5" fill={C.soft}>
        Sailing week with
      </text>
      <text x="116" y="163" fontSize="11.5" fill={C.soft}>
        the family. Bliss!
      </text>
      <PublicBadge x={278} y={113} />

      {/* arrow down */}
      <line x1="180" y1="190" x2="180" y2="206" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" />
      <polyline points="175,202 180,208 185,202" fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />

      {/* attacker notes */}
      <rect x="16.5" y="214.5" width="327" height="68" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <rect x="16" y="220" width="3" height="57" rx="1.5" fill={C.red} />
      <text x="30" y="231" fontSize="10.5" fontWeight="700" fill={C.red} letterSpacing="0.6">
        ATTACKER NOTES
      </text>
      <text x="30" y="247" fontSize="11" fontFamily={MONO} fill={C.soft}>
        eats at: Luca's | family: wife, 2 kids
      </text>
      <text x="30" y="263" fontSize="11" fontFamily={MONO} fill={C.soft}>
        loves: sailing | works at: ...
      </text>
      <rect className="sr-anim-blink" x="190" y="253" width="1.5" height="12" rx="0.75" fill={C.red} />
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. tailored prize email + red flags                                  */
/* ------------------------------------------------------------------ */
function FlagRow({ y, label }: { y: number; label: string }): JSX.Element {
  return (
    <g>
      <path d={`M30 ${y - 5} l6 10.5 h-12 z`} fill="none" stroke={C.red} strokeWidth="1.4" strokeLinejoin="round" />
      <line x1="30" y1={y - 1.6} x2="30" y2={y + 1.8} stroke={C.red} strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="30" cy={y + 4.2} r="0.8" fill={C.red} />
      <text x="46" y={y + 4} fontSize="11.5" fill={C.text}>
        {label}
      </text>
    </g>
  );
}

export function TailoredLureArt(): JSX.Element {
  const H = 300;
  return (
    <Frame label="Example: a lure made just for you" aria="A prize email that uses the reader's name and hobby, with an attached document asking to enable content, and three red flags listed below" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Inbox
      </text>
      <rect x="262" y="38" width="82" height="20" rx="10" fill={C.red} fillOpacity="0.12" stroke={C.red} strokeOpacity="0.4" />
      <text x="303" y="52" fontSize="10.5" fontWeight="700" fill={C.red} textAnchor="middle">
        External
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      {/* email card */}
      <rect x="16.5" y="78.5" width="327" height="116" rx="9" fill={C.panel2} stroke={C.line} />
      <text x="28" y="96" fontSize="10.5" fill={C.muted}>
        From: Anniversary Prize Team
      </text>
      <text x="28" y="113" fontSize="12.5" fontWeight="700" fill={C.text}>
        Congratulations, Marco. You won!
      </text>
      <text x="28" y="130" fontSize="11" fill={C.soft}>
        Your prize: a sailing trip for two.
      </text>
      {/* personal details the attacker already knew */}
      <text x="28" y="146" fontSize="10.5" fill={C.amber}>
        It knows your name and your hobby.
      </text>
      <rect x="28" y="156" width="190" height="28" rx="6" fill={C.code} stroke={C.line} />
      <rect x="36" y="162" width="14" height="17" rx="2" fill="none" stroke={C.mint} strokeWidth="1.4" />
      <line x1="39" y1="168" x2="47" y2="168" stroke={C.mint} strokeWidth="1.2" />
      <line x1="39" y1="172" x2="47" y2="172" stroke={C.mint} strokeWidth="1.2" />
      <text x="58" y="174" fontSize="11" fontFamily={MONO} fill={C.soft}>
        claim_form.docm
      </text>
      <rect className="sr-anim-pulse" x="230" y="158" width="104" height="24" rx="12" fill={C.red} fillOpacity="0.14" stroke={C.red} strokeOpacity="0.5" style={{ transformOrigin: "282px 170px" }} />
      <text x="282" y="174" fontSize="11" fontWeight="700" fill={C.red} textAnchor="middle">
        Enable Content
      </text>

      {/* red flags */}
      <text x="16" y="213" fontSize="10.5" fontWeight="700" fill={C.muted} letterSpacing="0.6">
        RED FLAGS
      </text>
      <FlagRow y={232} label="A prize you never entered for" />
      <line x1="16" y1="244.5" x2="344" y2="244.5" stroke={C.line} strokeOpacity="0.6" />
      <FlagRow y={258} label="A document that asks to Enable Content" />
      <line x1="16" y1="270.5" x2="344" y2="270.5" stroke={C.line} strokeOpacity="0.6" />
      <FlagRow y={284} label="A browser warning you are told to click past" />
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. the attack chain and its off-ramps                                */
/* ------------------------------------------------------------------ */
interface Step {
  title: string;
  sub: string;
  off: string;
}

const STEPS: Step[] = [
  { title: "Research", sub: "public posts and reviews", off: "Post less" },
  { title: "Lure", sub: "tailored prize email", off: "Verify first" },
  { title: "Foothold", sub: "macro document", off: "Don't enable" },
  { title: "Hijack", sub: "fake login page", off: "Heed warning" },
  { title: "Payoff", sub: "data stolen and sold", off: "Report early" },
];

export function ChainArt(): JSX.Element {
  const H = 300;
  const top = 34;
  const pitch = 47;
  return (
    <Frame label="One attack, five links" aria="A targeted attack as five linked steps: research, lure, foothold, hijack and payoff, each with the action that breaks that link" height={H}>
      <line x1="30" y1={top + 21} x2="30" y2={top + 21 + pitch * 4} stroke={C.line} strokeWidth="2" />
      {STEPS.map((s, i) => {
        const y = top + i * pitch;
        const last = i === STEPS.length - 1;
        const tone = last ? C.red : C.mint;
        return (
          <g key={s.title}>
            <rect x="46.5" y={y + 0.5} width="297" height="41" rx="9" fill={C.panel2} stroke={C.line} />
            <circle cx="30" cy={y + 21} r="11" fill={C.panel} stroke={tone} strokeWidth="1.5" />
            <text x="30" y={y + 25} fontSize="11.5" fontWeight="700" fill={tone} textAnchor="middle">
              {i + 1}
            </text>
            <text x="58" y={y + 18} fontSize="12.5" fontWeight="700" fill={C.text}>
              {s.title}
            </text>
            <text x="58" y={y + 33} fontSize="10.5" fill={C.muted}>
              {s.sub}
            </text>
            <rect x="238" y={y + 9} width="96" height="23" rx="11.5" fill={tone} fillOpacity="0.12" stroke={tone} strokeOpacity="0.45" />
            <text x="286" y={y + 24} fontSize="10.5" fontWeight="700" fill={tone} textAnchor="middle">
              {s.off}
            </text>
          </g>
        );
      })}
      {/* attention ring on the first link, where it is cheapest to stop */}
      <circle className="sr-anim-ring" cx="30" cy={top + 21} r="11" fill="none" stroke={C.mint} strokeWidth="1.5" />
      <text x="180" y={top + pitch * 5 + 3} fontSize="11.5" fontWeight="600" fill={C.mint} textAnchor="middle">
        Break any one link and the attack stops.
      </text>
    </Frame>
  );
}

export const SPEAR_PHISHING_OSINT_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "The perfect bait is built from what you share.",
    illustration: OversharingArt,
    caption: "Small public details add up to a script.",
    sections: [
      {
        h: "What this is about",
        body: "You post a restaurant review and a holiday photo. Then you see the other side: an attacker turns those posts into an email made just for you, and uses it to get into the company.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who works with email, social media or company systems. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "Filters block spam. They struggle with a message that sounds personal and comes with a believable story. What you post is up to you, and so is what you do with a surprise that seems made for you. A person is the control that stops the chain.",
      },
    ],
  },
  {
    tab: "What to watch for",
    headline: "Where a targeted attack gives itself away.",
    illustration: TailoredLureArt,
    caption: "The more it fits you, the more you should slow down.",
    sections: [
      {
        h: "Public details are research fuel",
        body: "Reviews, photos and profiles can be searched by anyone. Name, family, hobbies and workplace combine into a ready-made story for the attacker.",
      },
      {
        h: "A surprise that feels tailor-made",
        body: "A prize that matches your interests is not luck. Check it through a channel you find yourself, never through the message.",
      },
      {
        h: "\u201CEnable Content\u201D means run it",
        body: "A document that asks you to enable macros or editing is asking to run its code. Don\u2019t. Report it instead.",
      },
      {
        h: "A certificate warning is an alarm",
        body: "When a site you trust shows a privacy warning, someone may be intercepting you. Stop, don\u2019t click through, and report it.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: ChainArt,
    caption: "Every step has an off-ramp.",
    sections: [
      { h: "Spear phishing", body: "A phishing message written for one person or company, using real details about them. It looks made for you because it was." },
      { h: "OSINT", body: "Open-source intelligence: facts gathered from public sources such as reviews, social media and leaked data." },
      { h: "Digital footprint", body: "Everything about you that is public online. The bigger it is, the more an attacker has to work with." },
      { h: "Lure", body: "The bait: a message or offer built to make you reply, click or open something." },
      { h: "Macro", body: "Code hidden inside a document. \u201CEnable Content\u201D lets it run on your computer." },
      { h: "Certificate warning", body: "A browser alert that a site can\u2019t prove who it is. It can mean someone is intercepting the connection." },
      { h: "Session hijack", body: "Taking over a live sign-in, so the attacker acts as you without needing your password again." },
    ],
  },
];
